import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { QueryClient } from '@tanstack/angular-query-experimental';

import { combineLatest } from 'rxjs';

import {
  ObjectWidgetsScope,
  objectWidgetsQueryKey,
} from 'src/app/api/queries/widget.queries';
import {
  StagedWidgetLayout,
  applyStagedLayout,
} from 'src/app/shared/utils/widget-list.utils';

import { Widget } from '../models/core.models';
import { selectCurrentObject, selectSelectedPageId } from '../reducers';

/**
 * Imperative access to the widget list the current view renders. The views own
 * `injectObjectWidgetsQuery`; services and effects read it through here instead.
 * There is no second copy of the list: `snapshot()` reads the same cache entry the
 * mounted view is observing.
 *
 * The scope mirrors what the view computes: the current object plus, for a
 * dashboard, its selected page. `selectSelectedPageId` already returns null for
 * anything that is not a dashboard, so a feed's scope is `pageId: null`.
 */
@Injectable({ providedIn: 'root' })
export class CurrentViewWidgetsService {
  private readonly store = inject(Store);
  private readonly queryClient = inject(QueryClient);
  private readonly destroyRef = inject(DestroyRef);

  private currentScope: ObjectWidgetsScope | null = null;

  constructor() {
    combineLatest([
      this.store.select(selectCurrentObject),
      this.store.select(selectSelectedPageId),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([object, pageId]) => {
        this.currentScope = object?.id
          ? {
              objectId: object.id,
              objectClassId: object.object_class_id,
              pageId: pageId ?? null,
            }
          : null;
      });
  }

  /** The key every read/patch below targets. Null before the first object load. */
  scope(): ObjectWidgetsScope | null {
    return this.currentScope;
  }

  /** The list as cached right now. Empty when nothing is (or has been) loaded. */
  snapshot(): Widget[] {
    const scope = this.currentScope;
    if (!scope) return [];

    return (
      this.queryClient.getQueryData<Widget[]>(objectWidgetsQueryKey(scope)) ??
      []
    );
  }

  byId(widgetId: string): Widget | undefined {
    return this.snapshot().find((widget) => widget.id === widgetId);
  }

  /**
   * Replace one widget in the cached list with a server-confirmed copy. A no-op
   * when the list isn't cached, i.e. when nothing is rendering it.
   */
  patch(widgetId: string, update: (widget: Widget) => Widget): void {
    this.write((widgets) =>
      widgets.some((widget) => widget.id === widgetId)
        ? widgets.map((widget) =>
            widget.id === widgetId ? update(widget) : widget,
          )
        : widgets,
    );
  }

  /**
   * Commit staged geometry into the cached list once the server has accepted it
   * (`WidgetLayoutStagingService`'s flush), on **this view's** key only.
   */
  applyLayout(staged: ReadonlyMap<string, StagedWidgetLayout>): void {
    this.write((widgets) => applyStagedLayout(widgets, staged));
  }

  /** Drop widgets from the cached list (delete, and move-out of the view). */
  remove(widgetIds: string[]): void {
    const dropped = new Set(widgetIds);

    this.write((widgets) =>
      widgets.filter((widget) => !dropped.has(widget.id)),
    );
  }

  /** Refetch the list - used where the server derives fields we can't compute. */
  invalidate(): void {
    const scope = this.currentScope;
    if (!scope) return;

    this.queryClient.invalidateQueries({
      queryKey: objectWidgetsQueryKey(scope),
    });
  }

  /**
   * Forget a dashboard page's list. Page ids are reused within a dashboard, so
   * leaving the entry behind could paint a deleted page's widgets for one frame
   * on a later page that happens to take the same id.
   */
  removePage(pageId: number): void {
    const scope = this.currentScope;
    if (!scope) return;

    this.queryClient.removeQueries({
      queryKey: objectWidgetsQueryKey({ ...scope, pageId }),
    });
  }

  private write(update: (widgets: Widget[]) => Widget[]): void {
    const scope = this.currentScope;
    if (!scope) return;

    const key = objectWidgetsQueryKey(scope);
    const widgets = this.queryClient.getQueryData<Widget[]>(key);
    if (!widgets) return;

    this.queryClient.setQueryData<Widget[]>(key, update(widgets));
  }
}
